import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { Button, EmptyState, ErrorState } from '@bb/ui';
import { ApiError, type CampaignListFilters, type CampaignSummary } from '../../api/client.ts';
import { Can } from '../access/Can.tsx';
import { formatCount } from '../audience/ChannelReachBreakdown.tsx';
import { formatWhen } from '../segments/SegmentsListPage.tsx';
import { CampaignActions } from './CampaignActions.tsx';
import { CampaignStatusBadge } from './CampaignStatusBadge.tsx';
import { ColumnsPicker } from '../lists/ColumnsPicker.tsx';
import { IdleBanner } from '../lists/IdleBanner.tsx';
import { CAMPAIGN_COLUMNS, DEFAULT_CAMPAIGN_COLUMNS, type CampaignColumn } from './columns.ts';
import { CreateCampaignDialog } from './CreateCampaignDialog.tsx';
import { CHANNEL_LABELS, DELIVERY_LABELS } from './labels.ts';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
export const COLUMNS_PREFERENCE = 'campaigns.list.columns';
const STATUS_OPTIONS: Array<[NonNullable<CampaignListFilters['status']> | '', string]> = [
  ['', 'All but archived'],
  ['draft', 'Draft'],
  ['scheduled', 'Scheduled'],
  ['active', 'Active'],
  ['idle', 'Idle'],
  ['paused', 'Paused'],
  ['stopped', 'Stopped'],
  ['completed', 'Completed'],
  ['archived', 'Archived'],
  ['all', 'All'],
];

function schedule(c: CampaignSummary) {
  if (c.deliveryType !== 'scheduled') return DELIVERY_LABELS[c.deliveryType];
  return c.startAt ? `From ${formatWhen(c.startAt)}` : c.publishedRevision ? 'At launch' : '—';
}

const CELLS: Record<CampaignColumn, (c: CampaignSummary) => ReactNode> = {
  type: (c) => DELIVERY_LABELS[c.deliveryType],
  channels: (c) => c.channels.map((ch) => CHANNEL_LABELS[ch]).join(', '),
  schedule,
  stopDate: (c) => (c.endAt ? formatWhen(c.endAt) : '—'),
  sent: (c) => formatCount(c.sent),
  tags: (c) =>
    c.tags.map((t) => (
      <span key={t.id} className="tag">
        {t.name}
      </span>
    )),
  updated: (c) => `${formatWhen(c.updatedAt)}${c.updatedBy?.email ? ` by ${c.updatedBy.email}` : ''}`,
  createdBy: (c) => c.createdBy?.email ?? '—',
};

/**
 * Messaging → Campaigns (BB-96): status, idle and filters by type, channel, tag and search; optional columns saved per
 * employee; the idle banner; row actions follow the status and the employee's permissions.
 */
export function CampaignsListPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/campaigns' });
  const { api } = useRouteContext({ from: '/env/$environmentId/campaigns' });
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const [status, setStatus] = useState<CampaignListFilters['status'] | ''>('');
  const [deliveryType, setDeliveryType] = useState<CampaignListFilters['deliveryType'] | ''>('');
  const [channel, setChannel] = useState<CampaignListFilters['channel'] | ''>('');
  const [tagId, setTagId] = useState('');
  const [sort, setSort] = useState<NonNullable<CampaignListFilters['sort']>>('updated');
  const [creating, setCreating] = useState(false);
  const filters: CampaignListFilters = { sort, ...(query ? { query } : {}), ...(status ? { status } : {}), ...(deliveryType ? { deliveryType } : {}), ...(channel ? { channel } : {}), ...(tagId ? { tagId } : {}) };
  const list = useInfiniteQuery({
    queryKey: ['campaigns', environmentId, filters],
    queryFn: ({ pageParam }) => api.listCampaigns(environmentId, { ...filters, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const tags = useQuery({ queryKey: ['tags', environmentId], queryFn: () => api.listTags(environmentId) });
  const saved = useQuery({ queryKey: ['preference', COLUMNS_PREFERENCE], queryFn: () => api.getPreference<CampaignColumn[]>(COLUMNS_PREFERENCE) });
  const known = new Set<string>(CAMPAIGN_COLUMNS.map((c) => c.id));
  const columns = Array.isArray(saved.data?.value) ? saved.data.value.filter((c) => known.has(c)) : DEFAULT_CAMPAIGN_COLUMNS;
  const saveColumns = useMutation({
    mutationFn: (next: CampaignColumn[]) => api.putPreference(COLUMNS_PREFERENCE, next),
    onMutate: (next) => queryClient.setQueryData(['preference', COLUMNS_PREFERENCE], { key: COLUMNS_PREFERENCE, value: next }),
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const first = list.data?.pages[0];
  const filtered = Boolean(query || status || deliveryType || channel || tagId);

  return (
    <section>
      <h1>Campaigns</h1>
      <p className="hint">One-off, action-based and API-triggered messages. A running campaign keeps its published version while you edit a draft.</p>
      <Can permission="campaigns.edit">
        <Button variant="primary" onClick={() => setCreating(true)}>
          Create campaign
        </Button>
      </Can>
      {first && status !== 'idle' && <IdleBanner count={first.idleCount} idleDays={first.idleDays} noun={['campaign', 'campaigns']} onShow={() => setStatus('idle')} />}
      <div className="filters">
        <input aria-label="Search campaigns" placeholder="Search by name, description or API id" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          {STATUS_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select aria-label="Filter by campaign type" value={deliveryType} onChange={(e) => setDeliveryType(e.target.value as typeof deliveryType)}>
          <option value="">All types</option>
          {Object.entries(DELIVERY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select aria-label="Filter by channel" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
          <option value="">All channels</option>
          {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select aria-label="Filter by tag" value={tagId} onChange={(e) => setTagId(e.target.value)}>
          <option value="">All tags</option>
          {(tags.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <select aria-label="Sort by" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="updated">Last edited</option>
          <option value="created">Created</option>
          <option value="name">Name</option>
        </select>
        <ColumnsPicker columns={CAMPAIGN_COLUMNS} value={columns} onChange={(next) => saveColumns.mutate(next)} />
      </div>
      {list.isPending ? (
        <p aria-busy="true">Loading campaigns…</p>
      ) : list.isError ? (
        <ErrorState title="Campaigns could not be loaded" message={errorText(list.error)} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title={filtered ? 'No matching campaigns' : 'No campaigns yet'} description={filtered ? 'Nothing matches the filters.' : 'Create the first campaign to message an audience.'} />
      ) : (
        <>
          <table className="table" aria-label="Campaigns">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Status</th>
                {columns.map((id) => (
                  <th key={id} scope="col">
                    {CAMPAIGN_COLUMNS.find((c) => c.id === id)!.label}
                  </th>
                ))}
                <th scope="col">
                  <span className="bb-visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to="/env/$environmentId/campaigns/$campaignId" params={{ environmentId, campaignId: c.id }}>
                      {c.name}
                    </Link>
                    {c.description && <div className="hint">{c.description}</div>}
                  </td>
                  <td>
                    <CampaignStatusBadge campaign={c} />
                    {c.hasDraftChanges && c.status !== 'draft' && <span className="hint"> · unpublished changes</span>}
                  </td>
                  {columns.map((id) => (
                    <td key={id}>{CELLS[id](c)}</td>
                  ))}
                  <td>
                    <CampaignActions environmentId={environmentId} campaign={c} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.hasNextPage && (
            <Button variant="ghost" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
              Load more
            </Button>
          )}
        </>
      )}
      {saveColumns.isError && <p role="alert">Columns could not be saved: {errorText(saveColumns.error)}</p>}
      <CreateCampaignDialog environmentId={environmentId} open={creating} onOpenChange={setCreating} />
    </section>
  );
}
